import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { Box, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import type { UseQueryResult } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';

/** A titled section surface with an icon tile, an optional count and header action. */
export function SectionCard({
  icon,
  title,
  count,
  action,
  children,
}: {
  icon: ReactNode;
  title: string;
  count?: number;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2.25, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.75 }}>
        <Box
          aria-hidden
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
          {title}
        </Typography>
        {typeof count === 'number' ? <Chip size="small" label={count} /> : null}
        {action}
      </Stack>
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Paper>
  );
}

/** Compact inline empty state used inside a section body (lighter than the page-level EmptyState). */
export function SectionEmpty({ title, description }: { title: string; description?: string }) {
  return (
    <Box sx={{ py: 2, textAlign: 'center' }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {description ? (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      ) : null}
    </Box>
  );
}

function SectionLoading({ label }: { label: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" role="status" sx={{ py: 2, color: 'text.secondary' }}>
      <CircularProgress size={16} />
      <Typography variant="body2">{label}</Typography>
    </Stack>
  );
}

function SectionError({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTranslation('customers');
  return (
    <Box role="alert" sx={{ py: 1.5 }}>
      <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
        {t('sections.failedToLoad')}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {message}
      </Typography>
      {onRetry ? (
        <Box
          component="button"
          type="button"
          onClick={onRetry}
          sx={{
            display: 'block',
            mt: 0.5,
            border: 'none',
            background: 'none',
            p: 0,
            color: 'primary.main',
            cursor: 'pointer',
            font: 'inherit',
            textDecoration: 'underline',
          }}
        >
          {t('sections.retry')}
        </Box>
      ) : null}
    </Box>
  );
}

/** Renders the loading / error / empty / populated states of a list-returning query. */
export function ListSection<Item>({
  query,
  loadingLabel,
  emptyTitle,
  emptyDescription,
  getKey,
  renderItem,
}: {
  query: UseQueryResult<Item[], NormalizedApiError>;
  loadingLabel?: string;
  emptyTitle: string;
  emptyDescription?: string;
  getKey: (item: Item, index: number) => string;
  renderItem: (item: Item) => ReactNode;
}) {
  const { t } = useTranslation('customers');
  if (query.isLoading) {
    return <SectionLoading label={loadingLabel ?? t('common:status.loading')} />;
  }
  if (query.isError) {
    return (
      <SectionError
        message={query.error?.message ?? t('common:error.unknown')}
        onRetry={() => void query.refetch()}
      />
    );
  }
  const items = query.data ?? [];
  if (!items.length) {
    return <SectionEmpty title={emptyTitle} description={emptyDescription} />;
  }
  return (
    <Stack spacing={1.25}>
      {items.map((item, index) => (
        <Box key={getKey(item, index)}>{renderItem(item)}</Box>
      ))}
    </Stack>
  );
}

/** A bordered record row: title, optional subtitle, a chip row and free-form detail rows. */
export function RecordCard({
  title,
  subtitle,
  chips,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  chips?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
      <Stack spacing={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
        >
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                {subtitle}
              </Typography>
            ) : null}
          </Stack>
          {chips ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent="flex-end">
              {chips}
            </Stack>
          ) : null}
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}

/** A `label: value` line for record details. */
export function KeyValue({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Stack direction="row" spacing={1} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Stack>
  );
}
